"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.DEFAULT_RADIUS = void 0;
exports.getArea = getArea;
exports.getDistance = getDistance;
exports.getLength = getLength;
exports.offset = offset;

var _GeometryType = _interopRequireDefault(require("./geom/GeometryType.js"));

var _math = require("./math.js");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

/**
 * @module ol/sphere
 */

/**
 * Object literal with options for the {@link getLength} or {@link getArea}
 * functions.
 * @typedef {Object} SphereMetricOptions
 * @property {import("./proj.js").ProjectionLike} [projection='EPSG:3857']
 * Projection of the  geometry.  By default, the geometry is assumed to be in
 * Web Mercator.
 * @property {number} [radius=6371008.8] Sphere radius.  By default, the
 * [mean Earth radius](https://en.wikipedia.org/wiki/Earth_radius#Mean_radius)
 * for the WGS84 ellipsoid is used.
 */

/**
 * The mean Earth radius (1/3 * (2a + b)) for the WGS84 ellipsoid.
 * https://en.wikipedia.org/wiki/Earth_radius#Mean_radius
 * @type {number}
 */
var DEFAULT_RADIUS = 6371008.8;
/**
 * Get the great circle distance (in meters) between two geographic coordinates.
 * @param {Array} c1 Starting coordinate.
 * @param {Array} c2 Ending coordinate.
 * @param {number} [opt_radius] The sphere radius to use.  Defaults to the Earth's
 *     mean radius using the WGS84 ellipsoid.
 * @return {number} The great circle distance between the points (in meters).
 * @api
 */

exports.DEFAULT_RADIUS = DEFAULT_RADIUS;

function getDistance(c1, c2, opt_radius) {
  var radius = opt_radius || DEFAULT_RADIUS;
  var lat1 = (0, _math.toRadians)(c1[1]);
  var lat2 = (0, _math.toRadians)(c2[1]);
  var deltaLatBy2 = (lat2 - lat1) / 2;
  var deltaLonBy2 = (0, _math.toRadians)(c2[0] - c1[0]) / 2;
  var a = Math.sin(deltaLatBy2) * Math.sin(deltaLatBy2) + Math.sin(deltaLonBy2) * Math.sin(deltaLonBy2) * Math.cos(lat1) * Math.cos(lat2);
  return 2 * radius * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
/**
 * Get the cumulative great circle length of linestring coordinates (geographic).
 * @param {Array} coordinates Linestring coordinates.
 * @param {number} radius The sphere radius to use.
 * @return {number} The length (in meters).
 */


function getLengthInternal(coordinates, radius) {
  var length = 0;

  for (var i = 0, ii = coordinates.length; i < ii - 1; ++i) {
    length += getDistance(coordinates[i], coordinates[i + 1], radius);
  }

  return length;
}
/**
 * Get the spherical length of a geometry.  This length is the sum of the
 * great circle distances between coordinates.  For polygons, the length is
 * the sum of all rings.  For points, the length is zero.  For multi-part
 * geometries, the length is the sum of the length of each part.
 * @param {import("./geom/Geometry.js").default} geometry A geometry.
 * @param {SphereMetricOptions} [opt_options] Options for the
 * length calculation.  By default, geometries are assumed to be in 'EPSG:3857'.
 * You can change this by providing a `projection` option.
 * @return {number} The spherical length (in meters).
 * @api
 */


function getLength(geometry, opt_options) {
  var options = opt_options || {};
  var radius = options.radius || DEFAULT_RADIUS;
  var projection = options.projection || 'EPSG:3857';
  var type = geometry.getType();

  if (type !== _GeometryType["default"].GEOMETRY_COLLECTION) {
    geometry = geometry.clone().transform(projection, 'EPSG:4326');
  }

  var length = 0;
  var coordinates, coords, i, ii, j, jj;

  switch (type) {
    case _GeometryType["default"].POINT:
    case _GeometryType["default"].MULTI_POINT:
      {
        break;
      }

    case _GeometryType["default"].LINE_STRING:
    case _GeometryType["default"].LINEAR_RING:
      {
        coordinates =
        /** @type {import("./geom/SimpleGeometry.js").default} */
        geometry.getCoordinates();
        length = getLengthInternal(coordinates, radius);
        break;
      }

    case _GeometryType["default"].MULTI_LINE_STRING:
    case _GeometryType["default"].POLYGON:
      {
        coordinates =
        /** @type {import("./geom/SimpleGeometry.js").default} */
        geometry.getCoordinates();

        for (i = 0, ii = coordinates.length; i < ii; ++i) {
          length += getLengthInternal(coordinates[i], radius);
        }

        break;
      }

    case _GeometryType["default"].MULTI_POLYGON:
      {
        coordinates =
        /** @type {import("./geom/SimpleGeometry.js").default} */
        geometry.getCoordinates();

        for (i = 0, ii = coordinates.length; i < ii; ++i) {
          coords = coordinates[i];

          for (j = 0, jj = coords.length; j < jj; ++j) {
            length += getLengthInternal(coords[j], radius);
          }
        }

        break;
      }

    case _GeometryType["default"].GEOMETRY_COLLECTION:
      {
        var geometries =
        /** @type {import("./geom/GeometryCollection.js").default} */
        geometry.getGeometries();

        for (i = 0, ii = geometries.length; i < ii; ++i) {
          length += getLength(geometries[i], opt_options);
        }

        break;
      }

    default:
      {
        throw new Error('Unsupported geometry type: ' + type);
      }
  }

  return length;
}
/**
 * Returns the spherical area for a list of coordinates.
 *
 * [Reference](https://trs.jpl.nasa.gov/handle/2014/40409)
 * Robert. G. Chamberlain and William H. Duquette, "Some Algorithms for
 * Polygons on a Sphere", JPL Publication 07-03, Jet Propulsion
 * Laboratory, Pasadena, CA, June 2007
 *
 * @param {Array<import("./coordinate.js").Coordinate>} coordinates List of coordinates of a linear
 * ring. If the ring is oriented clockwise, the area will be positive,
 * otherwise it will be negative.
 * @param {number} radius The sphere radius.
 * @return {number} Area (in square meters).
 */


function getAreaInternal(coordinates, radius) {
  var area = 0;
  var len = coordinates.length;
  var x1 = coordinates[len - 1][0];
  var y1 = coordinates[len - 1][1];

  for (var i = 0; i < len; i++) {
    var x2 = coordinates[i][0];
    var y2 = coordinates[i][1];
    area += (0, _math.toRadians)(x2 - x1) * (2 + Math.sin((0, _math.toRadians)(y1)) + Math.sin((0, _math.toRadians)(y2)));
    x1 = x2;
    y1 = y2;
  }

  return area * radius * radius / 2.0;
}
/**
 * Get the spherical area of a geometry.  This is the area (in meters) assuming
 * that polygon edges are segments of great circles on a sphere.
 * @param {import("./geom/Geometry.js").default} geometry A geometry.
 * @param {SphereMetricOptions} [opt_options] Options for the area
 *     calculation.  By default, geometries are assumed to be in 'EPSG:3857'.
 *     You can change this by providing a `projection` option.
 * @return {number} The spherical area (in square meters).
 * @api
 */


function getArea(geometry, opt_options) {
  var options = opt_options || {};
  var radius = options.radius || DEFAULT_RADIUS;
  var projection = options.projection || 'EPSG:3857';
  var type = geometry.getType();

  if (type !== _GeometryType["default"].GEOMETRY_COLLECTION) {
    geometry = geometry.clone().transform(projection, 'EPSG:4326');
  }

  var area = 0;
  var coordinates, coords, i, ii, j, jj;

  switch (type) {
    case _GeometryType["default"].POINT:
    case _GeometryType["default"].MULTI_POINT:
    case _GeometryType["default"].LINE_STRING:
    case _GeometryType["default"].MULTI_LINE_STRING:
    case _GeometryType["default"].LINEAR_RING:
      {
        break;
      }

    case _GeometryType["default"].POLYGON:
      {
        coordinates =
        /** @type {import("./geom/Polygon.js").default} */
        geometry.getCoordinates();
        area = Math.abs(getAreaInternal(coordinates[0], radius));

        for (i = 1, ii = coordinates.length; i < ii; ++i) {
          area -= Math.abs(getAreaInternal(coordinates[i], radius));
        }

        break;
      }

    case _GeometryType["default"].MULTI_POLYGON:
      {
        coordinates =
        /** @type {import("./geom/SimpleGeometry.js").default} */
        geometry.getCoordinates();

        for (i = 0, ii = coordinates.length; i < ii; ++i) {
          coords = coordinates[i];
          area += Math.abs(getAreaInternal(coords[0], radius));

          for (j = 1, jj = coords.length; j < jj; ++j) {
            area -= Math.abs(getAreaInternal(coords[j], radius));
          }
        }

        break;
      }

    case _GeometryType["default"].GEOMETRY_COLLECTION:
      {
        var geometries =
        /** @type {import("./geom/GeometryCollection.js").default} */
        geometry.getGeometries();

        for (i = 0, ii = geometries.length; i < ii; ++i) {
          area += getArea(geometries[i], opt_options);
        }

        break;
      }

    default:
      {
        throw new Error('Unsupported geometry type: ' + type);
      }
  }

  return area;
}
/**
 * Returns the coordinate at the given distance and bearing from `c1`.
 *
 * @param {import("./coordinate.js").Coordinate} c1 The origin point (`[lon, lat]` in degrees).
 * @param {number} distance The great-circle distance between the origin
 *     point and the target point.
 * @param {number} bearing The bearing (in radians).
 * @param {number} [opt_radius] The sphere radius to use.  Defaults to the Earth's
 *     mean radius using the WGS84 ellipsoid.
 * @return {import("./coordinate.js").Coordinate} The target point.
 */


function offset(c1, distance, bearing, opt_radius) {
  var radius = opt_radius || DEFAULT_RADIUS;
  var lat1 = (0, _math.toRadians)(c1[1]);
  var lon1 = (0, _math.toRadians)(c1[0]);
  var dByR = distance / radius;
  var lat = Math.asin(Math.sin(lat1) * Math.cos(dByR) + Math.cos(lat1) * Math.sin(dByR) * Math.cos(bearing));
  var lon = lon1 + Math.atan2(Math.sin(bearing) * Math.sin(dByR) * Math.cos(lat1), Math.cos(dByR) - Math.sin(lat1) * Math.sin(lat));
  return [(0, _math.toDegrees)(lon), (0, _math.toDegrees)(lat)];
}
//# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJtYXBwaW5ncyI6Ijs7Ozs7Ozs7Ozs7QUFHQTs7QUFDQTs7OztBQUpBOzs7O0FBTUE7Ozs7Ozs7Ozs7OztBQVlBOzs7OztBQUtPLElBQU1BLGNBQWMsR0FBRyxTQUF2QjtBQUVQOzs7Ozs7Ozs7Ozs7QUFTTSxTQUFVQyxXQUFWLENBQXNCQyxFQUF0QixFQUEwQkMsRUFBMUIsRUFBOEJDLFVBQTlCLEVBQXdDO0VBQzVDLElBQU1DLE1BQU0sR0FBR0QsVUFBVSxJQUFJSixjQUE3QjtFQUNBLElBQU1NLElBQUksR0FBRyxxQkFBVUosRUFBRSxDQUFDLENBQUQsQ0FBWixDQUFiO0VBQ0EsSUFBTUssSUFBSSxHQUFHLHFCQUFVSixFQUFFLENBQUMsQ0FBRCxDQUFaLENBQWI7RUFDQSxJQUFNSyxXQUFXLEdBQUcsQ0FBQ0QsSUFBSSxHQUFHRCxJQUFSLElBQWdCLENBQXBDO0VBQ0EsSUFBTUcsV0FBVyxHQUFHLHFCQUFVTixFQUFFLENBQUMsQ0FBRCxDQUFGLEdBQVFELEVBQUUsQ0FBQyxDQUFELENBQXBCLElBQTJCLENBQS9DO0VBQ0EsSUFBTVEsQ0FBQyxHQUNMQyxJQUFJLENBQUNDLEdBQUwsQ0FBU0osV0FBVCxJQUF3QkcsSUFBSSxDQUFDQyxHQUFMLENBQVNKLFdBQVQsQ0FBeEIsR0FDQUcsSUFBSSxDQUFDQyxHQUFMLENBQVNILFdBQVQsSUFDRUUsSUFBSSxDQUFDQyxHQUFMLENBQVNILFdBQVQsQ0FERixHQUVFRSxJQUFJLENBQUNFLEdBQUwsQ0FBU1AsSUFBVCxDQUZGLEdBR0VLLElBQUksQ0FBQ0UsR0FBTCxDQUFTTixJQUFULENBTEo7RUFNQSxPQUFPLElBQUlGLE1BQUosR0FBYU0sSUFBSSxDQUFDRyxLQUFMLENBQVdILElBQUksQ0FBQ0ksSUFBTCxDQUFVTCxDQUFWLENBQVgsRUFBeUJDLElBQUksQ0FBQ0ksSUFBTCxDQUFVLElBQUlMLENBQWQsQ0FBekIsQ0FBcEI7QUFDRDtBQUVEOzs7Ozs7OztBQU1BLFNBQVNNLGlCQUFULENBQTJCQyxXQUEzQixFQUF3Q1osTUFBeEMsRUFBOEM7RUFDNUMsSUFBSWEsTUFBTSxHQUFHLENBQWI7O0VBQ0EsS0FBSyxJQUFJQyxDQUFDLEdBQUcsQ0FBUixFQUFXQyxFQUFFLEdBQUdILFdBQVcsQ0FBQ0MsTUFBakMsRUFBeUNDLENBQUMsR0FBR0MsRUFBRSxHQUFHLENBQWxELEVBQXFELEVBQUVELENBQXZELEVBQTBEO0lBQ3hERCxNQUFNLElBQUlqQixXQUFXLENBQUNnQixXQUFXLENBQUNFLENBQUQsQ0FBWixFQUFpQkYsV0FBVyxDQUFDRSxDQUFDLEdBQUcsQ0FBTCxDQUE1QixFQUFxQ2QsTUFBckMsQ0FBckI7RUFDRDs7RUFDRCxPQUFPYSxNQUFQO0FBQ0Q7QUFFRDs7Ozs7Ozs7Ozs7Ozs7QUFZTSxTQUFVRyxTQUFWLENBQW9CQyxRQUFwQixFQUE4QkMsV0FBOUIsRUFBeUM7RUFDN0MsSUFBTUMsT0FBTyxHQUFHRCxXQUFXLElBQUksRUFBL0I7RUFDQSxJQUFNbEIsTUFBTSxHQUFHbUIsT0FBTyxDQUFDbkIsTUFBUixJQUFrQkwsY0FBakM7RUFDQSxJQUFNeUIsVUFBVSxHQUFHRCxPQUFPLENBQUNDLFVBQVIsSUFBc0IsV0FBekM7RUFDQSxJQUFNQyxJQUFJLEdBQUdKLFFBQVEsQ0FBQ0ssT0FBVCxFQUFiOztFQUNBLElBQUlELElBQUksS0FBS0UseUJBQWFDLG1CQUExQixFQUErQztJQUM3Q1AsUUFBUSxHQUFHQSxRQUFRLENBQUNRLEtBQVQsR0FBaUJDLFNBQWpCLENBQTJCTixVQUEzQixFQUF1QyxXQUF2QyxDQUFYO0VBQ0Q7O0VBQ0QsSUFBSVAsTUFBTSxHQUFHLENBQWI7RUFDQSxJQUFJRCxXQUFKLEVBQWlCZSxNQUFqQixFQUF5QmIsQ0FBekIsRUFBNEJDLEVBQTVCLEVBQWdDYSxDQUFoQyxFQUFtQ0MsRUFBbkM7O0VBQ0EsUUFBUVIsSUFBUjtJQUNFLEtBQUtFLHlCQUFhTyxLQUFsQjtJQUNBLEtBQUtQLHlCQUFhUSxXQUFsQjtNQUErQjtRQUM3QjtNQUNEOztJQUNELEtBQUtSLHlCQUFhUyxXQUFsQjtJQUNBLEtBQUtULHlCQUFhVSxXQUFsQjtNQUErQjtRQUM3QnJCLFdBQVc7UUFBRztRQUNaSyxRQURzRSxDQUV0RWlCLGNBRnNFLEVBQXhFO1FBR0FyQixNQUFNLEdBQUdGLGlCQUFpQixDQUFDQyxXQUFELEVBQWNaLE1BQWQsQ0FBMUI7UUFDQTtNQUNEOztJQUNELEtBQUt1Qix5QkFBYVksaUJBQWxCO0lBQ0EsS0FBS1oseUJBQWFhLE9BQWxCO01BQTJCO1FBQ3pCeEIsV0FBVztRQUFHO1FBQ1pLLFFBRHNFLENBRXRFaUIsY0FGc0UsRUFBeEU7O1FBR0EsS0FBS3BCLENBQUMsR0FBRyxDQUFKLEVBQU9DLEVBQUUsR0FBR0gsV0FBVyxDQUFDQyxNQUE3QixFQUFxQ0MsQ0FBQyxHQUFHQyxFQUF6QyxFQUE2QyxFQUFFRCxDQUEvQyxFQUFrRDtVQUNoREQsTUFBTSxJQUFJRixpQkFBaUIsQ0FBQ0MsV0FBVyxDQUFDRSxDQUFELENBQVosRUFBaUJkLE1BQWpCLENBQTNCO1FBQ0Q7O1FBQ0Q7TUFDRDs7SUFDRCxLQUFLdUIseUJBQWFjLGFBQWxCO01BQWlDO1FBQy9CekIsV0FBVztRQUFHO1FBQ1pLLFFBRHNFLENBRXRFaUIsY0FGc0UsRUFBeEU7O1FBR0EsS0FBS3BCLENBQUMsR0FBRyxDQUFKLEVBQU9DLEVBQUUsR0FBR0gsV0FBVyxDQUFDQyxNQUE3QixFQUFxQ0MsQ0FBQyxHQUFHQyxFQUF6QyxFQUE2QyxFQUFFRCxDQUEvQyxFQUFrRDtVQUNoRGEsTUFBTSxHQUFHZixXQUFXLENBQUNFLENBQUQsQ0FBcEI7O1VBQ0EsS0FBS2MsQ0FBQyxHQUFHLENBQUosRUFBT0MsRUFBRSxHQUFHRixNQUFNLENBQUNkLE1BQXhCLEVBQWdDZSxDQUFDLEdBQUdDLEVBQXBDLEVBQXdDLEVBQUVELENBQTFDLEVBQTZDO1lBQzNDZixNQUFNLElBQUlGLGlCQUFpQixDQUFDZ0IsTUFBTSxDQUFDQyxDQUFELENBQVAsRUFBWTVCLE1BQVosQ0FBM0I7VUFDRDtRQUNGOztRQUNEO01BQ0Q7O0lBQ0QsS0FBS3VCLHlCQUFhQyxtQkFBbEI7TUFBdUM7UUFDckMsSUFBTWMsVUFBVTtRQUNkO1FBQ0VyQixRQUQ0RCxDQUU1RHNCLGFBRjRELEVBRGhFOztRQUlBLEtBQUt6QixDQUFDLEdBQUcsQ0FBSixFQUFPQyxFQUFFLEdBQUd1QixVQUFVLENBQUN6QixNQUE1QixFQUFvQ0MsQ0FBQyxHQUFHQyxFQUF4QyxFQUE0QyxFQUFFRCxDQUE5QyxFQUFpRDtVQUMvQ0QsTUFBTSxJQUFJRyxTQUFTLENBQUNzQixVQUFVLENBQUN4QixDQUFELENBQVgsRUFBZ0JJLFdBQWhCLENBQW5CO1FBQ0Q7O1FBQ0Q7TUFDRDs7SUFDRDtNQUFTO1FBQ1AsTUFBTSxJQUFJc0IsS0FBSixDQUFVLGdDQUFnQ25CLElBQTFDLENBQU47TUFDRDtFQS9DSDs7RUFpREEsT0FBT1IsTUFBUDtBQUNEO0FBRUQ7Ozs7Ozs7Ozs7Ozs7Ozs7QUFjQSxTQUFTNEIsZUFBVCxDQUF5QjdCLFdBQXpCLEVBQXNDWixNQUF0QyxFQUE0QztFQUMxQyxJQUFJMEMsSUFBSSxHQUFHLENBQVg7RUFDQSxJQUFNQyxHQUFHLEdBQUcvQixXQUFXLENBQUNDLE1BQXhCO0VBQ0EsSUFBSStCLEVBQUUsR0FBR2hDLFdBQVcsQ0FBQytCLEdBQUcsR0FBRyxDQUFQLENBQVgsQ0FBcUIsQ0FBckIsQ0FBVDtFQUNBLElBQUlFLEVBQUUsR0FBR2pDLFdBQVcsQ0FBQytCLEdBQUcsR0FBRyxDQUFQLENBQVgsQ0FBcUIsQ0FBckIsQ0FBVDs7RUFDQSxLQUFLLElBQUk3QixDQUFDLEdBQUcsQ0FBYixFQUFnQkEsQ0FBQyxHQUFHNkIsR0FBcEIsRUFBeUI3QixDQUFDLEVBQTFCLEVBQThCO0lBQzVCLElBQU1nQyxFQUFFLEdBQUdsQyxXQUFXLENBQUNFLENBQUQsQ0FBWCxDQUFlLENBQWYsQ0FBWDtJQUNBLElBQU1pQyxFQUFFLEdBQUduQyxXQUFXLENBQUNFLENBQUQsQ0FBWCxDQUFlLENBQWYsQ0FBWDtJQUNBNEIsSUFBSSxJQUNGLHFCQUFVSSxFQUFFLEdBQUdGLEVBQWYsS0FDQyxJQUFJdEMsSUFBSSxDQUFDQyxHQUFMLENBQVMscUJBQVVzQyxFQUFWLENBQVQsQ0FBSixHQUE4QnZDLElBQUksQ0FBQ0MsR0FBTCxDQUFTLHFCQUFVd0MsRUFBVixDQUFULENBRC9CLENBREY7SUFHQUgsRUFBRSxHQUFHRSxFQUFMO0lBQ0FELEVBQUUsR0FBR0UsRUFBTDtFQUNEOztFQUNELE9BQVFMLElBQUksR0FBRzFDLE1BQVAsR0FBZ0JBLE1BQWpCLEdBQTJCLEdBQWxDO0FBQ0Q7QUFFRDs7Ozs7Ozs7Ozs7O0FBVU0sU0FBVWdELE9BQVYsQ0FBa0IvQixRQUFsQixFQUE0QkMsV0FBNUIsRUFBdUM7RUFDM0MsSUFBTUMsT0FBTyxHQUFHRCxXQUFXLElBQUksRUFBL0I7RUFDQSxJQUFNbEIsTUFBTSxHQUFHbUIsT0FBTyxDQUFDbkIsTUFBUixJQUFrQkwsY0FBakM7RUFDQSxJQUFNeUIsVUFBVSxHQUFHRCxPQUFPLENBQUNDLFVBQVIsSUFBc0IsV0FBekM7RUFDQSxJQUFNQyxJQUFJLEdBQUdKLFFBQVEsQ0FBQ0ssT0FBVCxFQUFiOztFQUNBLElBQUlELElBQUksS0FBS0UseUJBQWFDLG1CQUExQixFQUErQztJQUM3Q1AsUUFBUSxHQUFHQSxRQUFRLENBQUNRLEtBQVQsR0FBaUJDLFNBQWpCLENBQTJCTixVQUEzQixFQUF1QyxXQUF2QyxDQUFYO0VBQ0Q7O0VBQ0QsSUFBSXNCLElBQUksR0FBRyxDQUFYO0VBQ0EsSUFBSTlCLFdBQUosRUFBaUJlLE1BQWpCLEVBQXlCYixDQUF6QixFQUE0QkMsRUFBNUIsRUFBZ0NhLENBQWhDLEVBQW1DQyxFQUFuQzs7RUFDQSxRQUFRUixJQUFSO0lBQ0UsS0FBS0UseUJBQWFPLEtBQWxCO0lBQ0EsS0FBS1AseUJBQWFRLFdBQWxCO0lBQ0EsS0FBS1IseUJBQWFTLFdBQWxCO0lBQ0EsS0FBS1QseUJBQWFZLGlCQUFsQjtJQUNBLEtBQUtaLHlCQUFhVSxXQUFsQjtNQUErQjtRQUM3QjtNQUNEOztJQUNELEtBQUtWLHlCQUFhYSxPQUFsQjtNQUEyQjtRQUN6QnhCLFdBQVc7UUFBRztRQUNaSyxRQUQrRCxDQUUvRGlCLGNBRitELEVBQWpFO1FBR0FRLElBQUksR0FBR3BDLElBQUksQ0FBQzJDLEdBQUwsQ0FBU1IsZUFBZSxDQUFDN0IsV0FBVyxDQUFDLENBQUQsQ0FBWixFQUFpQlosTUFBakIsQ0FBeEIsQ0FBUDs7UUFDQSxLQUFLYyxDQUFDLEdBQUcsQ0FBSixFQUFPQyxFQUFFLEdBQUdILFdBQVcsQ0FBQ0MsTUFBN0IsRUFBcUNDLENBQUMsR0FBR0MsRUFBekMsRUFBNkMsRUFBRUQsQ0FBL0MsRUFBa0Q7VUFDaEQ0QixJQUFJLElBQUlwQyxJQUFJLENBQUMyQyxHQUFMLENBQVNSLGVBQWUsQ0FBQzdCLFdBQVcsQ0FBQ0UsQ0FBRCxDQUFaLEVBQWlCZCxNQUFqQixDQUF4QixDQUFSO1FBQ0Q7O1FBQ0Q7TUFDRDs7SUFDRCxLQUFLdUIseUJBQWFjLGFBQWxCO01BQWlDO1FBQy9CekIsV0FBVztRQUFHO1FBQ1pLLFFBRHNFLENBRXRFaUIsY0FGc0UsRUFBeEU7O1FBR0EsS0FBS3BCLENBQUMsR0FBRyxDQUFKLEVBQU9DLEVBQUUsR0FBR0gsV0FBVyxDQUFDQyxNQUE3QixFQUFxQ0MsQ0FBQyxHQUFHQyxFQUF6QyxFQUE2QyxFQUFFRCxDQUEvQyxFQUFrRDtVQUNoRGEsTUFBTSxHQUFHZixXQUFXLENBQUNFLENBQUQsQ0FBcEI7VUFDQTRCLElBQUksSUFBSXBDLElBQUksQ0FBQzJDLEdBQUwsQ0FBU1IsZUFBZSxDQUFDZCxNQUFNLENBQUMsQ0FBRCxDQUFQLEVBQVkzQixNQUFaLENBQXhCLENBQVI7O1VBQ0EsS0FBSzRCLENBQUMsR0FBRyxDQUFKLEVBQU9DLEVBQUUsR0FBR0YsTUFBTSxDQUFDZCxNQUF4QixFQUFnQ2UsQ0FBQyxHQUFHQyxFQUFwQyxFQUF3QyxFQUFFRCxDQUExQyxFQUE2QztZQUMzQ2MsSUFBSSxJQUFJcEMsSUFBSSxDQUFDMkMsR0FBTCxDQUFTUixlQUFlLENBQUNkLE1BQU0sQ0FBQ0MsQ0FBRCxDQUFQLEVBQVk1QixNQUFaLENBQXhCLENBQVI7VUFDRDtRQUNGOztRQUNEO01BQ0Q7O0lBQ0QsS0FBS3VCLHlCQUFhQyxtQkFBbEI7TUFBdUM7UUFDckMsSUFBTWMsVUFBVTtRQUNkO1FBQ0VyQixRQUQ0RCxDQUU1RHNCLGFBRjRELEVBRGhFOztRQUlBLEtBQUt6QixDQUFDLEdBQUcsQ0FBSixFQUFPQyxFQUFFLEdBQUd1QixVQUFVLENBQUN6QixNQUE1QixFQUFvQ0MsQ0FBQyxHQUFHQyxFQUF4QyxFQUE0QyxFQUFFRCxDQUE5QyxFQUFpRDtVQUMvQzRCLElBQUksSUFBSU0sT0FBTyxDQUFDVixVQUFVLENBQUN4QixDQUFELENBQVgsRUFBZ0JJLFdBQWhCLENBQWY7UUFDRDs7UUFDRDtNQUNEOztJQUNEO01BQVM7UUFDUCxNQUFNLElBQUlzQixLQUFKLENBQVUsZ0NBQWdDbkIsSUFBMUMsQ0FBTjtNQUNEO0VBM0NIOztFQTZDQSxPQUFPcUIsSUFBUDtBQUNEO0FBRUQ7Ozs7Ozs7Ozs7Ozs7QUFXTSxTQUFVUSxNQUFWLENBQWlCckQsRUFBakIsRUFBcUJzRCxRQUFyQixFQUErQkMsT0FBL0IsRUFBd0NyRCxVQUF4QyxFQUFrRDtFQUN0RCxJQUFNQyxNQUFNLEdBQUdELFVBQVUsSUFBSUosY0FBN0I7RUFDQSxJQUFNTSxJQUFJLEdBQUcscUJBQVVKLEVBQUUsQ0FBQyxDQUFELENBQVosQ0FBYjtFQUNBLElBQU13RCxJQUFJLEdBQUcscUJBQVV4RCxFQUFFLENBQUMsQ0FBRCxDQUFaLENBQWI7RUFDQSxJQUFNeUQsSUFBSSxHQUFHSCxRQUFRLEdBQUduRCxNQUF4QjtFQUNBLElBQU11RCxHQUFHLEdBQUdqRCxJQUFJLENBQUNrRCxJQUFMLENBQ1ZsRCxJQUFJLENBQUNDLEdBQUwsQ0FBU04sSUFBVCxJQUFpQkssSUFBSSxDQUFDRSxHQUFMLENBQVM4QyxJQUFULENBQWpCLEdBQ0VoRCxJQUFJLENBQUNFLEdBQUwsQ0FBU1AsSUFBVCxJQUFpQkssSUFBSSxDQUFDQyxHQUFMLENBQVMrQyxJQUFULENBQWpCLEdBQWtDaEQsSUFBSSxDQUFDRSxHQUFMLENBQVM0QyxPQUFULENBRjFCLENBQVo7RUFJQSxJQUFNSyxHQUFHLEdBQ1BKLElBQUksR0FDSi9DLElBQUksQ0FBQ0csS0FBTCxDQUNFSCxJQUFJLENBQUNDLEdBQUwsQ0FBUzZDLE9BQVQsSUFBb0I5QyxJQUFJLENBQUNDLEdBQUwsQ0FBUytDLElBQVQsQ0FBcEIsR0FBcUNoRCxJQUFJLENBQUNFLEdBQUwsQ0FBU1AsSUFBVCxDQUR2QyxFQUVFSyxJQUFJLENBQUNFLEdBQUwsQ0FBUzhDLElBQVQsSUFBaUJoRCxJQUFJLENBQUNDLEdBQUwsQ0FBU04sSUFBVCxJQUFpQkssSUFBSSxDQUFDQyxHQUFMLENBQVNnRCxHQUFULENBRnBDLENBRkY7RUFNQSxPQUFPLENBQUMscUJBQVVFLEdBQVYsQ0FBRCxFQUFpQixxQkFBVUYsR0FBVixDQUFqQixDQUFQO0FBQ0QiLCJuYW1lcyI6WyJERUZBVUxUX1JBRElVUyIsImdldERpc3RhbmNlIiwiYzEiLCJjMiIsIm9wdF9yYWRpdXMiLCJyYWRpdXMiLCJsYXQxIiwibGF0MiIsImRlbHRhTGF0QnkyIiwiZGVsdGFMb25CeTIiLCJhIiwiTWF0aCIsInNpbiIsImNvcyIsImF0YW4yIiwic3FydCIsImdldExlbmd0aEludGVybmFsIiwiY29vcmRpbmF0ZXMiLCJsZW5ndGgiLCJpIiwiaWkiLCJnZXRMZW5ndGgiLCJnZW9tZXRyeSIsIm9wdF9vcHRpb25zIiwib3B0aW9ucyIsInByb2plY3Rpb24iLCJ0eXBlIiwiZ2V0VHlwZSIsIkdlb21ldHJ5VHlwZSIsIkdFT01FVFJZX0NPTExFQ1RJT04iLCJjbG9uZSIsInRyYW5zZm9ybSIsImNvb3JkcyIsImoiLCJqaiIsIlBPSU5UIiwiTVVMVElfUE9JTlQiLCJMSU5FX1NUUklORyIsIkxJTkVBUl9SSU5HIiwiZ2V0Q29vcmRpbmF0ZXMiLCJNVUxUSV9MSU5FX1NUUklORyIsIlBPTFlHT04iLCJNVUxUSV9QT0xZR09OIiwiZ2VvbWV0cmllcyIsImdldEdlb21ldHJpZXMiLCJFcnJvciIsImdldEFyZWFJbnRlcm5hbCIsImFyZWEiLCJsZW4iLCJ4MSIsInkxIiwieDIiLCJ5MiIsImdldEFyZWEiLCJhYnMiLCJvZmZzZXQiLCJkaXN0YW5jZSIsImJlYXJpbmciLCJsb24xIiwiZEJ5UiIsImxhdCIsImFzaW4iLCJsb24iXSwic291cmNlUm9vdCI6IiIsInNvdXJjZXMiOlsic3JjL3NwaGVyZS5qcyJdLCJzb3VyY2VzQ29udGVudCI6W251bGxdfQ==